import { NgModule } from '@angular/core';
import { TestRegistryService } from '../../services/test-registry.service';
import { MermaidService } from '../../../services/utils/mermaid.service';
import { TestControl } from '../../models/test-control.interface';

/**
 * Module for utility and service tests
 */
@NgModule({})
export class UtilityTestsModule {
  
  constructor(
    private testRegistry: TestRegistryService,
    private mermaidService: MermaidService
  ) {
    this.registerCategory();
    this.registerTests();
  }

  /**
   * Register the utility tests category
   */
  private registerCategory(): void {
    this.testRegistry.registerCategory({
      id: 'utility-tests',
      title: 'Utility Tests',
      description: 'Test utility services and helper functions',
      icon: 'build',
      color: 'success',
      order: 3
    });
  }

  /**
   * Register all utility test definitions
   */
  private registerTests(): void {
    const tests: TestControl[] = [
      {
        id: 'test-mermaid',
        title: 'Test Mermaid',
        description: 'Test Mermaid diagram service',
        category: 'utility-tests',
        type: 'button',
        icon: 'account_tree',
        execute: () => this.testMermaid()
      },
      {
        id: 'clear-console',
        title: 'Clear Console',
        description: 'Clear browser console output',
        category: 'utility-tests',
        type: 'button',
        icon: 'clear_all',
        execute: () => this.clearConsole()
      },
      {
        id: 'test-local-storage',
        title: 'Test Local Storage',
        description: 'Test browser local storage functionality',
        category: 'utility-tests',
        type: 'button',
        icon: 'storage',
        execute: () => this.testLocalStorage()
      },
      {
        id: 'performance-test',
        title: 'Performance Test',
        description: 'Run a basic performance measurement',
        category: 'utility-tests',
        type: 'button',
        icon: 'speed',
        execute: () => this.performanceTest()
      }
    ];
    
    tests.forEach(test => {
      this.testRegistry.registerTest(test);
    });
    
    console.log(`Registered ${tests.length} utility tests`);
  }

  /**
   * Test Mermaid service (migrated from runMermaidTest)
   */
  private async testMermaid(): Promise<void> {
    console.log('Running Mermaid test...');
    this.mermaidService.runTest();
  }

  /**
   * Clear console output
   */
  private async clearConsole(): Promise<void> {
    console.clear();
    console.log('Console cleared from utility test');
  }

  /**
   * Test local storage functionality
   */
  private async testLocalStorage(): Promise<void> {
    console.log('Testing local storage...');
    
    const testKey = 'test-bench-test';
    const testValue = { timestamp: Date.now(), data: 'test data' };
    
    try {
      // Test write
      localStorage.setItem(testKey, JSON.stringify(testValue));
      console.log('Local storage write successful');
      
      // Test read
      const retrieved = localStorage.getItem(testKey);
      if (retrieved) {
        const parsed = JSON.parse(retrieved);
        console.log('Local storage read successful:', parsed);
      }
      
      // Test delete
      localStorage.removeItem(testKey);
      console.log('Local storage cleanup successful');
      
    } catch (error) {
      console.error('Local storage test failed:', error);
      throw error;
    }
  }

  /**
   * Run basic performance test
   */
  private async performanceTest(): Promise<void> {
    console.log('Running performance test...');
    
    const startTime = performance.now();
    
    // Simulate some work
    let sum = 0;
    for (let i = 0; i < 1000000; i++) {
      sum += Math.random();
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    console.log(`Performance test completed in ${duration.toFixed(2)}ms`);
    console.log(`Computed sum: ${sum.toFixed(2)}`);
    
    // Memory usage (if available)
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      console.log('Memory usage:', {
        used: Math.round(memInfo.usedJSHeapSize / 1024 / 1024) + ' MB',
        total: Math.round(memInfo.totalJSHeapSize / 1024 / 1024) + ' MB',
        limit: Math.round(memInfo.jsHeapSizeLimit / 1024 / 1024) + ' MB'
      });
    }
  }
}